import {
    useTheme
} from "@fluentui/react";
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import FullCalendar from "@fullcalendar/react";
import { EventApi, EventDropArg, DateSpanApi } from "@fullcalendar/core"
import { EventReceiveArg } from "@fullcalendar/interaction"

import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import rrulePlugin from '@fullcalendar/rrule'
import { add, format, isEqual, startOfWeek, sub } from 'date-fns'
import './assets/scheduler.scss'
import { resourcesContext } from "../../providers/resources";
import interactionPlugin from "@fullcalendar/interaction"
import { EventRender, OtherSiteEventRender } from "./Events/EventRender";
import { DraftStatus, shiftsContext } from "../../providers/shifts";
import { Columns } from "./Columns/Columns";
import { EEventStatus, eventAllowed, eventDragStop } from "../../helpers/calendar";
import { Header } from "./Header/Header";
import Legend from './Legend/Legend';
import { SideMap, SideMapPoint } from "./SideMap/SideMap";
import { getResourceAccentColor, getResourceInitials } from "./resourceIdentity";
import { renderGenericFallbackUi } from '../error-boundary/generic-fallback-ui';
import { IEvent, IOtherSiteEvent, ITravelEvent } from "../../interfaces/event";
import { AppInsightsErrorBoundary } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from '../../applicationInsights';
import Swal from 'sweetalert2';
import { toast } from '../../helpers/notifications';
import { buildAIEmpowerPayload } from "../../helpers/aiEmpower/AIEmpowerPayloadBuilder";
import { authContext } from "../../providers/auth";
import { buildTravelEvents, buildTravelMeasureRequest, buildTravelSegments } from "../../helpers/travel/travelSegments";
import { ITravelMeasureResult } from "../../interfaces/travel";
import { boardContext } from "../../providers/boardContext";
import { useRsoConfiguration } from "../../providers/rsoConfigurationContext";
import { resolveAvantCareEnvHeader } from "../../helpers/avantCareEnv";
import {
    buildSchedulerResources,
    getSchedulerRowMetaFromResource,
    mapEventsToSchedulerRows,
    parseSchedulerResourceId,
    SchedulerDisplayEvent,
} from "../../helpers/scheduler/groupedResources";


export type TView = "resourceTimelineWeek" | "resourceTimelineFortnight"

const getSchedulerEventPriority = (event: Partial<SchedulerDisplayEvent> & { extendedProps?: any; display?: string | null | undefined; }): number => {
    if (event.display === 'contractedHours' || event.extendedProps?.availabilityType != null || event.display === 'background') {
        return 0;
    }

    if (event.display === 'travel' || event.extendedProps?.travelKind) {
        if (event.extendedProps?.travelKind === 'endBase') {
            return 3;
        }

        return 1;
    }

    return 2;
};

const compareSchedulerEvents = (left: any, right: any): number => {
    const priorityDiff = getSchedulerEventPriority(left) - getSchedulerEventPriority(right);
    if (priorityDiff !== 0) {
        return priorityDiff;
    }

    const leftStart = left?.start ? new Date(left.start).getTime() : 0;
    const rightStart = right?.start ? new Date(right.start).getTime() : 0;
    if (leftStart !== rightStart) {
        return leftStart - rightStart;
    }

    const leftEnd = left?.end ? new Date(left.end).getTime() : 0;
    const rightEnd = right?.end ? new Date(right.end).getTime() : 0;
    if (leftEnd !== rightEnd) {
        return rightEnd - leftEnd;
    }

    return String(left?.title ?? '').localeCompare(String(right?.title ?? ''));
};

const Scheduler = ({ openModal }: { openModal: (id: string) => void }) => {
    const { getAllResources, getIlluminaAvailablities } = useContext(resourcesContext)
    const { getAccessToken } = useContext(authContext)
    const { activeLevels } = useRsoConfiguration();
    const { isRegion, siteIds, sites, contextLabel } = useContext(boardContext)
    const {
        moveShift,
        getShiftsForCalendarView,
        getOtherSiteShiftsForCalendarView,
        loader,
        checkShiftLocationIsValid,
        checkShiftUnsavedChanges,
        markAllShiftsAsUnchanged,
        setCalendarDates,
        getShift,
        checkIsModified,
        draftStatus,
        getResourceData,
        checkRequiredCharacteristics,
        defineUserAvailability,
        startDate,
        endDate,
        showOtherSiteShifts,
        toggleShowOtherSiteShifts,
        loadDraft,
        view,
        setView,
        loadingPayrollData,
        siteLocation,
        siteLocationCoordinates,
        isCancellationReviewPending,
        validated,
        currentPayrollRange,
        getPayrollData,
        setRosterboardDragActive
    } = useContext(shiftsContext)

    const theme = useTheme();
    const apiBaseUrl = String(process.env.REACT_APP_API_URL ?? '/api').replace(/\/+$/, '');
    const apiEnvironment = resolveAvantCareEnvHeader();
    // Count how many requests are in flight.
    const [inFlightMoveCount, setInFlightMoveCount] = useState(0)
    const [showSideMap, setShowSideMap] = useState(false)
    // Short cooldown period - prevents rapid drags from fighting each other.
    const [isDndCooldown, setIsDndCooldown] = useState(false)
    const dndCooldownTimerRef = useRef<number | null>(null)
    const lastBusyToastAtRef = useRef<number>(0)
    const calendarRef = useRef<FullCalendar | null>(null);
    const calendarHostRef = useRef<HTMLDivElement | null>(null);
    const calendarResizeRafRef = useRef<number | null>(null);
    const sideMapSettleTimeoutRef = useRef<number | null>(null);
    const calendarScrollStateRef = useRef<Array<{ index: number; left: number; top: number }>>([]);
    // Cache the columns so we don't rebuild them on every render.
    const resourceAreaColumns = useMemo(
        () => Columns({ getResourceData, showOtherSiteShifts }),
        [getResourceData, showOtherSiteShifts]
    );

    const scheduleCalendarResize = React.useCallback(() => {
        if (calendarResizeRafRef.current != null) {
            window.cancelAnimationFrame(calendarResizeRafRef.current);
        }

        calendarResizeRafRef.current = window.requestAnimationFrame(() => {
            calendarResizeRafRef.current = null;
            const calendarApi = calendarRef.current?.getApi();
            const host = calendarHostRef.current;

            if (host) {
                const scrollgrid = host.querySelector('.fc-scrollgrid') as HTMLElement | null;
                const viewHarness = host.querySelector('.fc-view-harness') as HTMLElement | null;
                const root = host.querySelector('.fc') as HTMLElement | null;
                const scrollerHarnesses = Array.from(host.querySelectorAll('.fc-scroller-harness')) as HTMLElement[];
                const syncTables = Array.from(host.querySelectorAll('.fc-scrollgrid-sync-table')) as HTMLElement[];
                const timelineTables = Array.from(host.querySelectorAll('.fc-timeline-body table, .fc-timeline-header table')) as HTMLElement[];

                if (root) {
                    root.style.width = '100%';
                    root.style.minWidth = '0';
                }
                if (viewHarness) {
                    viewHarness.style.width = '100%';
                    viewHarness.style.minWidth = '0';
                }
                if (scrollgrid) {
                    scrollgrid.style.width = '100%';
                    scrollgrid.style.minWidth = '0';
                }
                scrollerHarnesses.forEach((element) => {
                    element.style.width = '100%';
                    element.style.minWidth = '0';
                });
                syncTables.forEach((element) => {
                    element.style.width = '100%';
                    element.style.minWidth = '100%';
                });
                timelineTables.forEach((element) => {
                    element.style.width = '100%';
                    element.style.minWidth = '100%';
                });
            }

            calendarApi?.updateSize();
        });
    }, []);

    const captureCalendarScrollState = React.useCallback(() => {
        const host = calendarHostRef.current;
        if (!host) {
            calendarScrollStateRef.current = [];
            return;
        }

        calendarScrollStateRef.current = Array
            .from(host.querySelectorAll('.fc-scroller'))
            .map((element, index) => {
                const scroller = element as HTMLElement;
                return {
                    index,
                    left: scroller.scrollLeft,
                    top: scroller.scrollTop
                };
            });
    }, []);

    const restoreCalendarScrollState = React.useCallback(() => {
        const host = calendarHostRef.current;
        if (!host || calendarScrollStateRef.current.length < 1) {
            return;
        }

        const scrollers = Array.from(host.querySelectorAll('.fc-scroller')) as HTMLElement[];
        calendarScrollStateRef.current.forEach(({ index, left, top }) => {
            const scroller = scrollers[index];
            if (!scroller) {
                return;
            }

            scroller.scrollLeft = left;
            scroller.scrollTop = top;
        });
    }, []);

    useEffect(() => {
        if (draftStatus === DraftStatus.found && validated) {
            handleShowLoadDraftPrompt()
        }
    }, [draftStatus, validated])

    useEffect(() => {
        return () => {
            if (calendarResizeRafRef.current != null) {
                window.cancelAnimationFrame(calendarResizeRafRef.current);
            }
            if (sideMapSettleTimeoutRef.current != null) {
                window.clearTimeout(sideMapSettleTimeoutRef.current);
            }
        };
    }, []);

    const handleSideMapLayoutSettled = React.useCallback(() => {
        if (sideMapSettleTimeoutRef.current != null) {
            window.clearTimeout(sideMapSettleTimeoutRef.current);
            sideMapSettleTimeoutRef.current = null;
        }

        window.setTimeout(() => {
            scheduleCalendarResize();
            restoreCalendarScrollState();
        }, 0);
    }, [restoreCalendarScrollState, scheduleCalendarResize]);

    useEffect(() => {
        const host = calendarHostRef.current;
        if (!host) {
            return;
        }

        const timeoutIds = [
            window.setTimeout(() => {
                scheduleCalendarResize();
                restoreCalendarScrollState();
            }, 0),
            window.setTimeout(() => {
                scheduleCalendarResize();
                restoreCalendarScrollState();
            }, 180),
            window.setTimeout(() => {
                scheduleCalendarResize();
                restoreCalendarScrollState();
            }, 360),
        ];

        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => {
                scheduleCalendarResize();
                restoreCalendarScrollState();
            });
            resizeObserver.observe(host);
        }

        window.addEventListener('resize', scheduleCalendarResize);

        return () => {
            timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
            resizeObserver?.disconnect();
            window.removeEventListener('resize', scheduleCalendarResize);
        };
    }, [restoreCalendarScrollState, scheduleCalendarResize, showSideMap]);


    const [travelEvents, setTravelEvents] = useState<ITravelEvent[]>([])

    const travelSegments = useMemo(() => {
        const resources = getAllResources() ?? [];
        const shifts = (getShiftsForCalendarView() ?? []).filter((shift) =>
            shift.resourceId !== '0' &&
            !shift.display &&
            shift.availabilityType == null
        );

        return buildTravelSegments(resources, shifts, siteLocationCoordinates);
    }, [getAllResources, getShiftsForCalendarView, siteLocationCoordinates]);

    useEffect(() => {
        const controller = new AbortController();
        let disposed = false;

        const loadTravelMeasurements = async () => {
            if (travelSegments.length < 1) {
                setTravelEvents([]);
                return;
            }

            setTravelEvents([]);

            try {
                const accessToken = await getAccessToken();
                if (!accessToken || controller.signal.aborted) {
                    return;
                }

                const response = await fetch(`${apiBaseUrl}/Travel/measure`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`,
                        'X-AvantCare-Env': apiEnvironment
                    },
                    body: JSON.stringify(buildTravelMeasureRequest(travelSegments)),
                    signal: controller.signal
                });

                if (!response.ok) {
                    throw new Error(`Travel measure request failed with status ${response.status}`);
                }

                const payload = await response.json() as ITravelMeasureResult;
                if (disposed || controller.signal.aborted) {
                    return;
                }

                setTravelEvents(buildTravelEvents(travelSegments, payload?.segments ?? []));
            } catch (error: any) {
                if (controller.signal.aborted || error?.name === 'AbortError') {
                    return;
                }

                console.error('Failed to load travel measurements.', error);
                setTravelEvents([]);
            }
        };

        loadTravelMeasurements();

        return () => {
            disposed = true;
            controller.abort();
        };
    }, [apiBaseUrl, apiEnvironment, getAccessToken, travelSegments]);

    const contractedHoursEvents = useMemo(() => {
        const availablities = getIlluminaAvailablities();
        return availablities.filter(item => {
            if (item.display  !== 'contractedHours') {
                return false;
            }

            const itemStart = new Date(item.start);
            const itemEnd = new Date(item.end);

            const hasDateOverlap = (
                (itemStart <= endDate && itemEnd >= startDate)
            );

            return hasDateOverlap;
        });
    }, [
        endDate,
        getIlluminaAvailablities,
        startDate,
    ]);

    // Build the event list for the visible date range.
    const displayedEvents = useMemo(() => {
        const shifts = getShiftsForCalendarView();
        const availablities = getIlluminaAvailablities();
        const getOtherAvailabilities = availablities.filter(item => item.display !== 'contractedHours');

        const newEventData = [
            ...getOtherAvailabilities,
            ...contractedHoursEvents,
            ...((showOtherSiteShifts && !isRegion) ? getOtherSiteShiftsForCalendarView() : []),
            ...shifts,
            ...travelEvents
        ];

        return mapEventsToSchedulerRows(
            newEventData as SchedulerDisplayEvent[],
            getAllResources() ?? [],
            siteIds,
            isRegion,
        ) as SchedulerDisplayEvent[];
    }, [
        getAllResources,
        getIlluminaAvailablities,
        getOtherSiteShiftsForCalendarView,
        getShiftsForCalendarView,
        isRegion,
        siteIds,
        showOtherSiteShifts,
        contractedHoursEvents,
        travelEvents,
    ])

    const aiEmpowerPayload = useMemo(() => {
        const resources = getAllResources() ?? [];
        const shifts = getShiftsForCalendarView() ?? [];
        const planningWindow = {
            start: startDate.toISOString(),
            end: endDate.toISOString()
        };
        return buildAIEmpowerPayload({
            resources,
            shifts,
            planningWindow,
            siteBaseLocation: siteLocationCoordinates,
            rsoConstraintLevels: activeLevels
        });
    }, [activeLevels, getAllResources, getShiftsForCalendarView, startDate, endDate, siteLocationCoordinates]);

    useEffect(() => {
        if (!aiEmpowerPayload) return;
        if (typeof window !== "undefined") {
            (window as any).__AIEmpowerPayload = aiEmpowerPayload;
        }
    }, [aiEmpowerPayload]);

    // Keep a ref copy so drag handlers can read without re-rendering.
    const displayedEventsRef = useRef<any[]>([])
    useEffect(() => {
        displayedEventsRef.current = displayedEvents as any[]
    }, [displayedEvents])

    // When dragging, we can "freeze" the UI list to avoid jitter.
    const [isCalendarUiFrozen, setIsCalendarUiFrozen] = useState(false)
    const frozenEventsRef = useRef<any[]>([])
    const [calendarEventsState, setCalendarEventsState] = useState<SchedulerDisplayEvent[]>([])
    const calendarEventsRef = useRef<SchedulerDisplayEvent[]>([])
    // Only these fields require a full event rebuild in FullCalendar.
    const structuralKeys = useMemo(() => new Set(["id", "start", "end", "resourceId", "allDay", "display"]), [])

    // Decide if we need to rebuild the event list or can do small updates.
    const hasStructuralChanges = React.useCallback(
        (prevEvents: SchedulerDisplayEvent[], nextEvents: SchedulerDisplayEvent[]) => {
            if (prevEvents.length !== nextEvents.length) return true;
            const prevMap = new Map<string, SchedulerDisplayEvent>();
            prevEvents.forEach((ev) => prevMap.set(ev.id, ev));
            if (prevMap.size !== nextEvents.length) return true;
            for (const next of nextEvents) {
                const prev = prevMap.get(next.id);
                if (!prev) return true;
                const prevStart = prev.start ? new Date(prev.start).getTime() : null;
                const nextStart = next.start ? new Date(next.start).getTime() : null;
                const prevEnd = prev.end ? new Date(prev.end).getTime() : null;
                const nextEnd = next.end ? new Date(next.end).getTime() : null;
                if (prevStart !== nextStart || prevEnd !== nextEnd) return true;
                if ((prev as any).resourceId !== (next as any).resourceId) return true;
                if ((prev as any).display !== (next as any).display) return true;
                if ((prev as any).allDay !== (next as any).allDay) return true;
            }
            return false;
        },
        []
    );

    // Update only titles/extended props so we avoid rebuilding events and triggering visual jumps.
    const applyMinorEventUpdates = React.useCallback(
        (prevEvents: SchedulerDisplayEvent[], nextEvents: SchedulerDisplayEvent[]) => {
            const api = calendarRef.current?.getApi();
            if (!api) return false;
            const prevMap = new Map<string, SchedulerDisplayEvent>();
            prevEvents.forEach((ev) => prevMap.set(ev.id, ev));
            // The FullCalendar API exposes batchRendering even though the typings omit it.
            (api as any).batchRendering(() => {
                nextEvents.forEach((next) => {
                    const prev = prevMap.get(next.id);
                    if (!prev) return;
                    const calEvent = api.getEventById(next.id);
                    if (!calEvent) return;
                    if (prev.title !== next.title && next.title != null) {
                        calEvent.setProp("title", next.title);
                    }
                    Object.keys(next).forEach((key) => {
                        if (structuralKeys.has(key) || key === "title") return;
                        const prevVal = (prev as any)[key];
                        const nextVal = (next as any)[key];
                        if (prevVal !== nextVal) {
                            calEvent.setExtendedProp(key, nextVal);
                        }
                    });
                });
            });
            return true;
        },
        [structuralKeys]
    );

    // Keep the FullCalendar event list in sync with minimal churn.
    const syncCalendarEvents = React.useCallback(
        (nextEvents: SchedulerDisplayEvent[]) => {
            const prevEvents = calendarEventsRef.current;
            if (!prevEvents.length) {
                setCalendarEventsState(nextEvents);
                calendarEventsRef.current = nextEvents;
                return;
            }
            if (hasStructuralChanges(prevEvents, nextEvents)) {
                setCalendarEventsState(nextEvents);
                calendarEventsRef.current = nextEvents;
                return;
            }
            const applied = applyMinorEventUpdates(prevEvents, nextEvents);
            if (applied) {
                calendarEventsRef.current = nextEvents;
            } else {
                setCalendarEventsState(nextEvents);
                calendarEventsRef.current = nextEvents;
            }
        },
        [applyMinorEventUpdates, hasStructuralChanges]
    );

    useEffect(() => {
        // Freeze the list while dragging to avoid UI jumps.
        if (isCalendarUiFrozen) {
            const frozen = frozenEventsRef.current as SchedulerDisplayEvent[];
            setCalendarEventsState(frozen);
            calendarEventsRef.current = frozen;
            return;
        }
        syncCalendarEvents(displayedEvents as SchedulerDisplayEvent[]);
    }, [displayedEvents, isCalendarUiFrozen, syncCalendarEvents]);

    // Memoize events and resources to avoid unnecessary re-renders
    const memoizedEvents = useMemo(() => calendarEventsState, [calendarEventsState])
    const actualResources = useMemo(() => getAllResources() ?? [], [getAllResources])
    const memoizedResources = useMemo(
        () => buildSchedulerResources(actualResources, sites, isRegion),
        [actualResources, isRegion, sites]
    )

    const resourceIdentityById = useMemo(() => {
        const map = new Map<string, { name: string; color: string; initials: string }>();
        actualResources.forEach((resource) => {
            map.set(resource.id, {
                name: resource.title,
                color: getResourceAccentColor({
                    resourceId: resource.id,
                    title: resource.title,
                    index: resource.index
                }),
                initials: getResourceInitials(resource.title)
            });
        });
        map.set('0', {
            name: 'Unassigned',
            color: getResourceAccentColor({ resourceId: '0', title: 'Unassigned', index: 0 }),
            initials: getResourceInitials('Unassigned')
        });
        return map;
    }, [actualResources])

    const mapPoints = useMemo(() => {
        const points: SideMapPoint[] = [];

        if (isRegion) {
            sites.forEach((site) => {
                const latitude = site.illumina_latitudes == null ? null : Number(site.illumina_latitudes);
                const longitude = site.illumina_longitudes == null ? null : Number(site.illumina_longitudes);
                if (!Number.isFinite(latitude as number) || !Number.isFinite(longitude as number)) {
                    return;
                }
                points.push({
                    id: `site-${site.illumina_sitelocationid}`,
                    pointType: 'site',
                    lat: latitude as number,
                    lng: longitude as number,
                    title: site.illumina_name,
                    color: '#cf541e',
                    initials: getResourceInitials(site.illumina_name || 'Site'),
                    subtitle: 'Region site location'
                });
            });
        } else {
            const siteLatitude = siteLocationCoordinates?.latitude;
            const siteLongitude = siteLocationCoordinates?.longitude;
            if (Number.isFinite(siteLatitude as number) && Number.isFinite(siteLongitude as number)) {
                points.push({
                    id: 'current-site',
                    pointType: 'site',
                    lat: siteLatitude as number,
                    lng: siteLongitude as number,
                    title: siteLocation || 'Current Site',
                    color: '#cf541e',
                    initials: getResourceInitials(siteLocation || 'Site'),
                    subtitle: 'Current rosterboard site'
                });
            }
        }

        actualResources.forEach((resource) => {
            if (resource.id === '0') {
                return;
            }

            const latitude = resource.resourceAddressCoordinates?.latitude;
            const longitude = resource.resourceAddressCoordinates?.longitude;
            if (!Number.isFinite(latitude as number) || !Number.isFinite(longitude as number)) {
                return;
            }

            const resourceIdentity = resourceIdentityById.get(resource.id) ?? {
                name: resource.title,
                color: getResourceAccentColor({
                    resourceId: resource.id,
                    title: resource.title,
                    index: resource.index
                }),
                initials: getResourceInitials(resource.title)
            };

            points.push({
                id: `resource-${resource.id}`,
                pointType: 'resource',
                lat: latitude as number,
                lng: longitude as number,
                title: resourceIdentity.name,
                color: resourceIdentity.color,
                initials: resourceIdentity.initials,
                subtitle: resource.resourceAddressLabel ?? 'Address unavailable'
            });
        });

        return points;
    }, [actualResources, isRegion, resourceIdentityById, siteLocation, siteLocationCoordinates, sites])

    const toggleShowSideMap = () => {
        captureCalendarScrollState();
        setShowSideMap((current) => !current);

        if (sideMapSettleTimeoutRef.current != null) {
            window.clearTimeout(sideMapSettleTimeoutRef.current);
        }

        sideMapSettleTimeoutRef.current = window.setTimeout(() => {
            handleSideMapLayoutSettled();
        }, 220);
    }
    const resolveSchedulerTarget = React.useCallback((resourceLike: any) => {
        const meta = getSchedulerRowMetaFromResource(resourceLike);
        if (meta.rowType !== 'resource') {
            return null;
        }

        return {
            resourceId: meta.actualResourceId ?? resourceLike?.id,
            siteLocationId: meta.siteLocationId,
        };
    }, []);

    const handleShowLoadDraftPrompt = async () => {
        const result = await Swal.fire({
            title: 'Load Draft?',
            text: 'Do you want to load the saved draft?',
            icon: 'info',
            showDenyButton: true,
            confirmButtonText: 'Yes',
            denyButtonText: 'No',
            confirmButtonColor: 'green',
            denyButtonColor: 'red',
        })

        if (result.isConfirmed) {
            loadDraft();
        }
    }

    const handleShowUnsavedChangesPrompt = async () => {
        return await Swal.fire({
            title: 'Unsaved Changes',
            text: 'You have unsaved changes. If you click "Yes", your changes will be lost. Click "No" to save your changes first.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            cancelButtonColor: theme.palette.tealLight,
            confirmButtonColor: theme.palette.themePrimary,
        });
    }

    const views = {
        resourceTimelineWeek: {
            type: 'resourceTimeline',
            buttonText: 'Week',
            slotDuration: { day: 1 },
            duration: { days: 7 },
        },
        resourceTimelineFortnight: {
            type: 'resourceTimeline',
            slotDuration: { day: 1 },
            buttonText: 'Fortnight',
            duration: { days: 14 },
        }
    }

    const setCalendarDatesWithView = (date: Date) => {
        if (loadingPayrollData || checkShiftUnsavedChanges()) return;
        setCalendarDates(date, view)
    }

    const updateDateRange = async (dates: Date[]) => {
        if (loadingPayrollData) return;
        let isUnsavedChanges = checkShiftUnsavedChanges();
        if (isUnsavedChanges === false || (isUnsavedChanges === true && (await handleShowUnsavedChangesPrompt()).isConfirmed)) {
            if (!dates || dates.length === 0) return;

            markAllShiftsAsUnchanged();
            // Resolve payroll range for the clicked date and jump to that range if found.
            const result = await getPayrollData(dates[0], view);
            if (result?.startForView && result?.endForView) {
                calendarRef.current?.getApi().gotoDate(result.startForView)
                console.info(`Selected payroll cycle ${format(result.startForView, 'd/M/yyyy')} - ${format(result.endForView, 'd/M/yyyy')}`)
                return
            }

            if (view === "resourceTimelineWeek") {
                const startDate = startOfWeek(dates[0], { weekStartsOn: 1 });
                setCalendarDatesWithView(startDate)
                calendarRef.current?.getApi().gotoDate(startDate)
            } else {
                setCalendarDatesWithView(dates[0])
                calendarRef.current?.getApi().gotoDate(dates[0])
            }
        }
    }

    const getNavigationBaseDate = () => {
        const calendarDate = calendarRef.current?.getApi().getDate();
        if (calendarDate && !Number.isNaN(calendarDate.getTime())) {
            return calendarDate;
        }
        if (startDate && !Number.isNaN(startDate.getTime())) {
            return startDate;
        }
        return new Date();
    }

    const increaseDate = async () => {
        if (checkShiftUnsavedChanges() === false || (checkShiftUnsavedChanges() === true && (await handleShowUnsavedChangesPrompt()).isConfirmed)) {
            markAllShiftsAsUnchanged();
            const baseDate = getNavigationBaseDate();
            const nDate = add(baseDate, {
                weeks: view === "resourceTimelineWeek" ? 1 : 2
            })
            // Prefer payroll-resolved range; fall back to week-snap only if no cycle matched.
            const result = await getPayrollData(nDate, view);
            if (result?.startForView && !isEqual(result.startForView, startDate)) {
                calendarRef.current?.getApi().gotoDate(result.startForView)
                return
            }
            const fallbackDate = view === "resourceTimelineWeek" ? startOfWeek(nDate, { weekStartsOn: 1 }) : nDate;
            setCalendarDatesWithView(fallbackDate)
            calendarRef.current?.getApi().gotoDate(fallbackDate)
        }
    }

    const decreaseDate = async () => {
        if (checkShiftUnsavedChanges() === false || (checkShiftUnsavedChanges() === true && (await handleShowUnsavedChangesPrompt()).isConfirmed)) {
            markAllShiftsAsUnchanged();
            const baseDate = getNavigationBaseDate();
            const nDate = sub(baseDate, {
                weeks: view === "resourceTimelineWeek" ? 1 : 2
            })
            // Prefer payroll-resolved range; fall back to week-snap only if no cycle matched.
            const result = await getPayrollData(nDate, view);
            if (result?.startForView && !isEqual(result.startForView, startDate)) {
                calendarRef.current?.getApi().gotoDate(result.startForView)
                return
            }
            const fallbackDate = view === "resourceTimelineWeek" ? startOfWeek(nDate, { weekStartsOn: 1 }) : nDate;
            setCalendarDatesWithView(fallbackDate)
            calendarRef.current?.getApi().gotoDate(fallbackDate)
        }
    }

    const setToCurrentWeek = async () => {
        if (checkShiftUnsavedChanges() === false || (checkShiftUnsavedChanges() === true && (await handleShowUnsavedChangesPrompt()).isConfirmed)) {
            markAllShiftsAsUnchanged();
            // Jump to the correct payroll cycle for "today", with a week-view fallback.
            const nDate = view === "resourceTimelineWeek" ? startOfWeek(new Date(), { weekStartsOn: 1 }) : new Date();
            const result = await getPayrollData(nDate, view);
            if (result?.startForView) {
                calendarRef.current?.getApi().gotoDate(result.startForView)
                return
            }
            if (view === "resourceTimelineWeek") {
                calendarRef.current?.getApi().gotoDate(nDate)
                setCalendarDatesWithView(nDate)
            }
        }
    }

    const updateView = async (v: TView) => {
        setView(v)
        calendarRef.current?.getApi().changeView(v)
        // When switching views, prefer the payroll-resolved range for the new view.
        const result = await getPayrollData(startDate, v);
        if (result?.startForView) {
            calendarRef.current?.getApi().gotoDate(result.startForView)
            return
        }
        if (v === "resourceTimelineWeek") {
            const fallbackDate = startOfWeek(startDate, { weekStartsOn: 1 });
            setCalendarDates(fallbackDate, v)
            calendarRef.current?.getApi().gotoDate(fallbackDate)
        }
    }

    const eventClick = async (event: EventApi) => {
        if (event.display == 'background' || event.display === 'travel' || event.extendedProps?.availabilityType != null) return;

        if (!event.extendedProps.otherSite) {
            openModal(event.id);
        }
    }

    const openNewJobModal = () => {
        window.top?.postMessage({ type: 'ac_rb_new_job' }, '*');
    }

    // Combine any "global" loading states into a single overlay.
    const isBlockingLoading = loader?.loading || draftStatus === DraftStatus.publishing;
    const blockingMessage = draftStatus === DraftStatus.publishing ? "Publishing..." : (loader?.message ?? "Loading...");

    // Short delay to keep rapid drop/receive events from colliding.
    const startDndCooldown = () => {
        setIsDndCooldown(true)
        if (dndCooldownTimerRef.current != null) {
            window.clearTimeout(dndCooldownTimerRef.current)
        }
        dndCooldownTimerRef.current = window.setTimeout(() => {
            dndCooldownTimerRef.current = null
            setIsDndCooldown(false)
        }, 300)
    }

    useEffect(() => {
        // Clean up any pending cooldown timers on unmount.
        return () => {
            if (dndCooldownTimerRef.current != null) {
                window.clearTimeout(dndCooldownTimerRef.current)
                dndCooldownTimerRef.current = null
            }
        }
    }, [])

    // Local visual cue for drag state.
    const [dragIndicator, setDragIndicator] = useState<{ isActive: boolean, label: string }>({
        isActive: false,
        label: ''
    })
    const dragIndicatorElRef = useRef<HTMLDivElement | null>(null)
    const dragIndicatorPosRef = useRef<{ x: number, y: number }>({ x: 0, y: 0 })
    const dragIndicatorRafRef = useRef<number | null>(null)
    const dragStartTokenRef = useRef(0)
    const showDragIndicator = false



    // NOTE: we intentionally do not programmatically restore `.fc-scroller` positions here.
    // It can cause scroll-sync feedback loops (horizontal + vertical oscillation) in ScrollGrid.

    // Throttle pointermove updates so we don't overload the UI thread.
    useEffect(() => {
        if (!showDragIndicator || !dragIndicator.isActive) return

        let lastFrame = 0;
        const onPointerMove = (event: PointerEvent) => {
            const now = performance.now();
            if (now - lastFrame < 16) return; // ~60fps throttle
            lastFrame = now;
            if (dragIndicatorRafRef.current != null) return;
            dragIndicatorRafRef.current = requestAnimationFrame(() => {
                dragIndicatorRafRef.current = null;
                dragIndicatorPosRef.current = { x: event.clientX, y: event.clientY };
                const el = dragIndicatorElRef.current;
                if (el) {
                    el.style.transform = `translate3d(${event.clientX + 12}px, ${event.clientY + 12}px, 0)`;
                }
            });
        };

        window.addEventListener('pointermove', onPointerMove, true);

        const el = dragIndicatorElRef.current;
        if (el) {
            const { x, y } = dragIndicatorPosRef.current;
            el.style.transform = `translate3d(${x + 12}px, ${y + 12}px, 0)`;
        }
        return () => {
            window.removeEventListener('pointermove', onPointerMove, true);
            if (dragIndicatorRafRef.current != null) {
                cancelAnimationFrame(dragIndicatorRafRef.current);
                dragIndicatorRafRef.current = null;
            }
        };
    }, [dragIndicator.isActive]);

    function handleEventDrop(info: EventDropArg) {
        // If we're already processing a drop, block this one to avoid conflicts.
        if (isDndCooldown) {
            const now = Date.now();
            if (now - lastBusyToastAtRef.current > 1500) {
                lastBusyToastAtRef.current = now;
                toast('warning', 'Please wait a moment');
            }
            (info as any).revert?.();
            setIsCalendarUiFrozen(false);
            setRosterboardDragActive(false);
            setDragIndicator((prev) => ({ ...prev, isActive: false }));
            return;
        }
        if (info.event.extendedProps?.loading || info.event.extendedProps?.validating) {
            setIsCalendarUiFrozen(false);
            setRosterboardDragActive(false);
            setDragIndicator((prev) => ({ ...prev, isActive: false }));
            return;
        }

        // Start the move and keep UI state consistent during the async call.
        startDndCooldown();
        setInFlightMoveCount((c) => c + 1);
        const target = resolveSchedulerTarget(info.newResource);
        if (!target) {
            (info as any).revert?.();
            setIsCalendarUiFrozen(false);
            setRosterboardDragActive(false);
            setDragIndicator((prev) => ({ ...prev, isActive: false }));
            setInFlightMoveCount((c) => Math.max(0, c - 1));
            return;
        }
        moveShift(info.event.id, target.resourceId, undefined, undefined, target.siteLocationId)
            .then((success) => {
                if (!success) {
                    (info as any).revert?.();
                }
            })
            .catch(() => {
                // Always unfreeze UI on error
                setIsCalendarUiFrozen(false);
                setRosterboardDragActive(false);
                setDragIndicator((prev) => ({ ...prev, isActive: false }));
            })
            .finally(() => {
                setIsCalendarUiFrozen(false);
                setRosterboardDragActive(false);
                setDragIndicator((prev) => ({ ...prev, isActive: false }));
                setInFlightMoveCount((c) => Math.max(0, c - 1));
            });
    }

    function handleEventReceive(info: EventReceiveArg) {
        // Same guard as drop: avoid overlapping operations.
        if (isDndCooldown) {
            const now = Date.now();
            if (now - lastBusyToastAtRef.current > 1500) {
                lastBusyToastAtRef.current = now;
                toast('warning', 'Please wait a moment');
            }
            (info as any).revert?.();
            setIsCalendarUiFrozen(false);
            setRosterboardDragActive(false);
            setDragIndicator((prev) => ({ ...prev, isActive: false }));
            return;
        }
        if (info.event.extendedProps?.loading || info.event.extendedProps?.validating) {
            setIsCalendarUiFrozen(false);
            setRosterboardDragActive(false);
            setDragIndicator((prev) => ({ ...prev, isActive: false }));
            return;
        }
        const resources = info.event.getResources();
        if (Array.isArray(resources) && resources.length) {
            const target = resolveSchedulerTarget(resources[0]);
            if (!target) {
                (info as any).revert?.();
                setIsCalendarUiFrozen(false);
                setRosterboardDragActive(false);
                setDragIndicator((prev) => ({ ...prev, isActive: false }));
                return;
            }
            // Track the async move so the header can show a small counter.
            startDndCooldown();
            setInFlightMoveCount((c) => c + 1);
            moveShift(info.event.id, target.resourceId, undefined, undefined, target.siteLocationId)
                .then((success) => {
                    if (!success) {
                        (info as any).revert?.();
                    }
                })
                .catch(() => {
                    setIsCalendarUiFrozen(false);
                    setRosterboardDragActive(false);
                    setDragIndicator((prev) => ({ ...prev, isActive: false }));
                })
                .finally(() => {
                    setIsCalendarUiFrozen(false);
                    setRosterboardDragActive(false);
                    setDragIndicator((prev) => ({ ...prev, isActive: false }));
                    setInFlightMoveCount((c) => Math.max(0, c - 1));
                });
        }
    }

    function handleEventAllow(span: DateSpanApi, event: EventApi | null): boolean {
        if (event?.display === 'travel' || event?.extendedProps.loading || event?.extendedProps?.validating) {
            return false
        }
        if (isRegion) {
            const originalResourceMeta = getSchedulerRowMetaFromResource(event?.getResources()[0]);
            const targetMeta = parseSchedulerResourceId(span.resource?.id);
            return checkShiftLocationIsValid(
                span.start,
                span.end,
                event?.id,
                targetMeta.actualResourceId ?? span.resource?.id,
                originalResourceMeta.actualResourceId ?? event?.getResources()[0]?.id
            ) !== EEventStatus.error;
        }
        return eventAllowed(span, checkShiftLocationIsValid, calendarRef, event, defineUserAvailability)
    }
    

    return (
        <div>
                <div style={{ boxSizing: 'border-box', display: 'flex', flexDirection: 'column', height: '100vh' }}>
                <Header
                    increaseDate={increaseDate}
                    decreaseDate={decreaseDate}
                    setToCurrentWeek={setToCurrentWeek}
                    date={startDate}
                    updateDateRange={updateDateRange}
                    dateRange={[startDate, endDate]}
                    updateView={updateView}
                    view={view}
                    setDate={setCalendarDatesWithView}
                    showOtherSiteShifts={showOtherSiteShifts}
                    toggleShowOtherSitesShifts={toggleShowOtherSiteShifts}
                    loadingPayrollData={loadingPayrollData}
                    openNewJobModal={openNewJobModal}
                    inFlightCount={inFlightMoveCount}
                    payrollRange={currentPayrollRange}
                />
                <div
                    style={{ display: 'flex', flex: 1, minWidth: 0, overflow: 'hidden' }}
                    id={"calender"}
                    className={isDndCooldown ? 'rb-dnd-locked' : undefined}
                >
                    <div ref={calendarHostRef} style={{ flex: 1, minWidth: 0, height: '100%', overflow: 'hidden' }}>
                        <AppInsightsErrorBoundary
                            onError={() =>
                                renderGenericFallbackUi({
                                    mainHeading: 'Something went wrong whilst trying to load the scheduler',
                                    subHeading: 'Please try again in a few minutes'
                                })
                            }
                            appInsights={reactPlugin}
                        >
                            {/* FullCalendar is vendor code. See `patches/@fullcalendar+interaction+6.1.15.patch` + README for our local fix. */}
                            <FullCalendar
                                ref={calendarRef}
                                resourceAreaColumns={resourceAreaColumns}
                                plugins={[resourceTimelinePlugin, interactionPlugin, rrulePlugin]}
                                headerToolbar={false}
                                // Keep scrolling predictable while dragging.
                                dragScroll={false}
                                scrollTime={null}
                                scrollTimeReset={false}
                                nowIndicator={false}
                                stickyHeaderDates={false}
                                stickyFooterScrollbar={false}
                                eventDragMinDistance={5}
                                slotMinWidth={180}
                                height={'100%'}
                                initialDate={startDate}
                                eventDragStart={React.useCallback((info) => {
                                    // Capture details early so we can keep the drag UI stable.
                                    const dragStartToken = ++dragStartTokenRef.current;
                                    // If we're cooling down, block the drag and keep UI calm.
                                    if (isDndCooldown) {
                                        const now = Date.now();
                                        if (now - lastBusyToastAtRef.current > 1500) {
                                            lastBusyToastAtRef.current = now;
                                            toast('warning', 'Please wait a moment');
                                        }
                                        setIsCalendarUiFrozen(false);
                                        setRosterboardDragActive(false);
                                        setDragIndicator((prev) => ({ ...prev, isActive: false }));
                                        return;
                                    }
                                    // Pull pointer info so we can position the drag label.
                                    const jsEvent = (info as any).jsEvent as MouseEvent | PointerEvent | undefined;
                                    const clientX = jsEvent?.clientX;
                                    const clientY = jsEvent?.clientY;
                                    const dragLabel = info.event?.title ? info.event.title : '';

                                    // Freeze the event list during drag to avoid jumpy re-renders.
                                    frozenEventsRef.current = calendarEventsRef.current;
                                    requestAnimationFrame(() => {
                                        if (dragStartTokenRef.current !== dragStartToken) {
                                            return;
                                        }
                                        setIsCalendarUiFrozen(true);
                                        setRosterboardDragActive(true);

                                        // Keep the drag indicator near the pointer.
                                        if (clientX != null && clientY != null) {
                                            dragIndicatorPosRef.current = { x: clientX, y: clientY };
                                            if (dragIndicatorElRef.current) {
                                                dragIndicatorElRef.current.style.transform = `translate3d(${clientX + 12}px, ${clientY + 12}px, 0)`;
                                            }
                                        }

                                        // Show a small label so users know what they grabbed.
                                        setDragIndicator({
                                            isActive: true,
                                            label: dragLabel,
                                        });
                                    });
                                }, [isDndCooldown])}
                                eventDragStop={React.useCallback((info) => {
                                    // Invalidate any in-flight drag work and reset state.
                                    dragStartTokenRef.current += 1;
                                    setIsCalendarUiFrozen(false);
                                    setRosterboardDragActive(false);
                                    setDragIndicator((prev) => ({ ...prev, isActive: false }));
                                    eventDragStop(calendarRef);
                                }, [])}
                                // Subtle styling for days in the past.
                                slotLaneClassNames={React.useCallback((arg: any) => {
                                    const cellDate = new Date(arg.date);
                                    cellDate.setHours(0, 0, 0, 0);
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    return cellDate < today ? ['rb-past-slot'] : [];
                                }, [])}
                                slotLabelContent={React.useCallback((e) => {
                                    const date = new Date(e.date);
                                    return (<>{format(date, 'EEEE do')}</>);
                                }, [])}
                                slotLabelFormat={{ weekday: 'long', day: 'numeric' }}
                                schedulerLicenseKey={'CC-Attribution-NonCommercial-NoDerivatives'}
                                views={views}
                                // datesSet={(dateRange) => {
                                //     setCalendarDates(dateRange.start, view)
                                // }}
                                initialView={view}
                                resources={memoizedResources}
                                events={memoizedEvents}
                                resourceLabelClassNames={React.useCallback((arg: any) => {
                                    const rowMeta = getSchedulerRowMetaFromResource(arg.resource);
                                    if (rowMeta.rowType === 'site-group') {
                                        return ['rb-site-group-row', 'rb-site-group-label'];
                                    }
                                    if (rowMeta.isUnassigned) {
                                        return ['rb-unassigned-row', 'rb-unassigned-row-label'];
                                    }
                                    return [];
                                }, [])}
                                resourceLaneClassNames={React.useCallback((arg: any) => {
                                    const rowMeta = getSchedulerRowMetaFromResource(arg.resource);
                                    if (rowMeta.rowType === 'site-group') {
                                        return ['rb-site-group-row', 'rb-site-group-lane'];
                                    }
                                    if (rowMeta.isUnassigned) {
                                        return ['rb-unassigned-row', 'rb-unassigned-row-lane'];
                                    }
                                    return [];
                                }, [])}
                                eventClassNames={React.useCallback((arg) => {
                                    if (arg.event.display === 'contractedHours' || arg.event.extendedProps?.availabilityType != null || arg.event.display === 'background') {
                                        return ['fc-availability-event'];
                                    }

                                    if (arg.event.display === 'travel' || arg.event.extendedProps?.travelKind) {
                                        return [
                                            'fc-travel-event',
                                            `fc-travel-${String(arg.event.extendedProps?.travelKind ?? 'unknown')}`
                                        ];
                                    }

                                    return ['fc-shift-event'];
                                }, [])}
                                eventOrder={React.useCallback((left: any, right: any) => compareSchedulerEvents(left, right), [])}
                                eventOrderStrict={true}
                                eventContent={React.useCallback(({ event, isDragging }) => {
                                    if (event.extendedProps.otherSite) {
                                        return (<OtherSiteEventRender event={event} />);
                                    } else {
                                        return (
                                            <EventRender
                                                event={event}
                                                getShift={getShift}
                                                isDragging={isDragging}
                                                checkCharacteristics={checkRequiredCharacteristics}
                                                getResourceData={getResourceData}
                                                checkIsModified={checkIsModified}
                                                eventClick={eventClick}
                                                isCancellationReviewPending={isCancellationReviewPending}
                                            />
                                        );
                                    }
                                }, [getShift, checkRequiredCharacteristics, getResourceData, checkIsModified, eventClick, isCancellationReviewPending])}
                                eventColor={'transparent'}
                                eventTextColor={'black'}
                                editable={false}
                                eventDurationEditable={false}
                                eventResourceEditable={!isDndCooldown}
                                eventClick={(info) => {
                                    eventClick(info.event);
                                }}
                                resourceOrder={'index'}
                                resourceAreaWidth={350}
                                eventOverlap={true}
                                eventAllow={handleEventAllow}
                                eventDrop={handleEventDrop}
                                eventReceive={handleEventReceive}
                            />
                        </AppInsightsErrorBoundary>
                    </div>
                    <SideMap
                        isOpen={showSideMap}
                        onToggle={toggleShowSideMap}
                        onLayoutSettled={handleSideMapLayoutSettled}
                        points={mapPoints}
                        fallbackCenter={siteLocationCoordinates}
                    />
                </div>

                <Legend />
            </div>

            <div
                ref={dragIndicatorElRef}
                className="rb-drag-indicator"
                style={{ display: showDragIndicator && dragIndicator.isActive ? 'block' : 'none' }}
            >
                {dragIndicator.label}
            </div>

            {isBlockingLoading && (
                // Simple modal overlay to prevent edits while data is busy.
                <div className="rb-blocking-overlay" aria-live="polite" aria-busy="true">
                    <div className="rb-blocking-modal" role="dialog" aria-modal="true" aria-label={blockingMessage}>
                        <div className="rb-blocking-wheel" />
                        <div className="rb-blocking-text">{blockingMessage}</div>
                    </div>
                </div>
            )}
        </div>
    )
}


export { Scheduler }
